import React, { useState, useEffect } from 'react';

import ClubHero from './ClubHero';
import ClubOverview from './ClubOverview';
import ClubTabs from './ClubTabs';
import ClubValueGauge from './ClubValueGauge';
import ClubMetricSection from './ClubMetricSection';
import ClubStreakForm from './ClubStreakForm';
import ClubPlaceholderPanel from './ClubPlaceholderPanel';
import ClubArticles from './ClubArticles';
import ClubDesktopIdentity from './ClubDesktopIdentity';
import ClubLiveStanding from './ClubLiveStanding';
import ClubSquadStats from './ClubSquadStats';
import styles from './ClubProfile.module.css';
import { KLAVIYO_PUBLIC_KEY, KLAVIYO_LIST_ID } from '../../../server/services/custom-pages/constants';
import IdentityGate from '../custom-widgets/IdentityGate';
import { syncFromKlaviyo, store, subscribeEntityToKlaviyo } from '../../../server/services/custom-pages/auth';

// Stories are fetched via the /api/club-stories proxy to avoid CORS
async function fetchStoriesViaProxy(collectionSlug, entityId, tabSlug, clubSlug, clubName, hostname) {
    const res = await fetch(`${hostname}/api/club-stories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ collectionSlug, entityId, tabSlug, clubSlug, clubName })
    });
    if (!res.ok) throw new Error(`Proxy responded ${res.status}`);
    return res.json();
}

export default function ClubProfilePage({ club, adId, hostname, initialIsMember = false }) {
    const defaultIndex = club.tabs ? club.tabs.findIndex(t => t.toLowerCase() === 'transfers') : -1;
    const [activeTab, setActiveTab] = useState(defaultIndex !== -1 ? defaultIndex : null);
    const [articles, setArticles] = useState(club.articles || []);
    const [isArticlesLoading, setIsArticlesLoading] = useState(false);
    const [isMounted, setIsMounted] = useState(false);

    const [isMember, setIsMember] = useState(initialIsMember);
    const [isSyncing, setIsSyncing] = useState(!initialIsMember);
    const [isRevealing, setIsRevealing] = useState(false);
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        setIsMounted(true);
        const scriptId = 'stn-player-script';
        if (!document.getElementById(scriptId)) {
            const script = document.createElement('script');
            script.id = scriptId;
            script.src = `https://embed.sendtonews.com/player3/embedcode.js?fk=${adId || "j1dzuhuw"}`;
            script.async = true;
            document.body.appendChild(script);
        }

        syncFromKlaviyo().then(res => {
            if (res.isSignedIn && res.email) {
                setEmail(res.email);
                store.set("fi_was_signed_in", "true");

                // Trust local cookie first to avoid asynchronous Klaviyo indexing lag
                const hasLocalCookie = club.statscoreId 
                    ? document.cookie.includes(`klaviyo_followed_${club.statscoreId}=true`)
                    : false;

                const isFollowing = hasLocalCookie || !!(
                    club.statscoreId &&
                    res.followedEntities &&
                    res.followedEntities.some(e => String(e.id || e) === String(club.statscoreId))
                );

                if (isFollowing) {
                    setIsMember(true);
                    if (club.statscoreId) {
                        document.cookie = `klaviyo_followed_${club.statscoreId}=true; path=/; max-age=${60 * 60 * 24 * 365}`;
                    }
                } else {
                    setIsMember(false);
                    if (club.statscoreId) {
                        document.cookie = `klaviyo_followed_${club.statscoreId}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;`;
                    }
                    if (!hasLocalCookie) {
                        document.cookie = "klaviyo_member=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;";
                    }
                }
            } else {
                // Not signed in to site - rely on cookies
                const isMemberCookie = club.statscoreId 
                    ? document.cookie.includes(`klaviyo_followed_${club.statscoreId}=true`)
                    : false;
                if (isMemberCookie || initialIsMember) {
                    setIsMember(true);
                } else {
                    setIsMember(false);
                }
            }
            setIsSyncing(false);
        }).catch(err => {
            console.error('[ClubProfilePage] sync error:', err);
            setIsSyncing(false);
        });
    }, [initialIsMember, club.statscoreId]);

    const handleUnlock = async (e) => {
        e.preventDefault();
        if (!email || !email.includes('@')) {
            setEmailError('Please enter a valid email address.');
            return;
        }

        setIsSubmitting(true);
        setEmailError("");
        store.set("user_email", email);

        if (club.statscoreId) {
            document.cookie = `klaviyo_followed_${club.statscoreId}=true; path=/; max-age=${60 * 60 * 24 * 365}`;
        }
        document.cookie = "klaviyo_member=true; path=/; max-age=" + (60 * 60 * 24 * 365);

        setIsRevealing(true);
        setTimeout(() => {
            setIsMember(true);
            setIsRevealing(false);
            setIsSubmitting(false);
        }, 800);

        subscribeEntityToKlaviyo(email, {
            id: club.statscoreId,
            name: club.name || club.displayName || "",
            name_for_digest: club.displayName || club.name || "",
            entityType: 'club'
        }).catch(err => {
            console.warn("[ClubProfilePage] Klaviyo background subscription:", err);
        });
    };

    const handleTabChange = async (index, tabName) => {
        // Remove deselect toggle: clicking on already active tab does nothing
        if (activeTab === index) return;

        setActiveTab(index);
        setIsArticlesLoading(true);

        try {
            let tabSlug = tabName.toLowerCase().replace(/\s+/g, '-');
            if (tabName === 'Next Manager') {
                tabSlug = 'manager-pressure';
            }

            const newArticles = await fetchStoriesViaProxy(tabSlug, club.id, tabSlug, club.slug, club.name, hostname);
            setArticles(newArticles);
        } catch (err) {
            console.error('Failed to fetch filtered stories', err);
        } finally {
            setIsArticlesLoading(false);
        }
    };

    return (
        <main className={styles.page}>
            <div className={styles.phoneShell}>
                <ClubHero club={club} />

                {/* STN Video Player (Rendered once, shared by mobile and desktop) */}
                {isMounted && (
                    <section className={styles.clubVideoSection}>
                        <div className={styles.clubVideoPanel}>
                            <div className={`voltax-mp-video-tag-player ${styles.clubVideoPlayer}`} data-stn-player={adId || "j1dzuhuw"}></div>
                            
                        </div>
                    </section>
                )}

                <div className={styles.mobileOnly}>
                    <ClubTabs tabs={club.tabs} activeTab={activeTab} onTabChange={handleTabChange} />
                    <ClubValueGauge value={club.value} />

                    <div style={{ position: 'relative', marginTop: '24px' }}>
                        <IdentityGate
                            isMember={isMember}
                            isRevealing={isRevealing}
                            email={email}
                            setEmail={setEmail}
                            emailError={emailError}
                            setEmailError={setEmailError}
                            isSubmitting={isSubmitting}
                            handleUnlock={handleUnlock}
                            isSyncing={isSyncing}
                        />

                        <div className={`${styles['content-reveal']} ${!isMember ? styles['blurred-content-locked'] : ''}`}>
                            <ClubLiveStanding teams={club.miniStandings} />
                            <ClubMetricSection title="Attack Pulse" variant="pulse" metrics={[
                                { label: 'Team xG', value: club.technicalStats.xg },
                                { label: 'Actual Goals', value: club.technicalStats.goals }
                            ]} />
                            <ClubMetricSection title="Defensive Integrity" variant="cards" metrics={[
                                { label: 'Interceptions', value: club.technicalStats.interceptions },
                                { label: 'Errors', value: club.technicalStats.errorsLeadingToGoals }
                            ]} />
                            <ClubStreakForm streak={club.streak} />
                            <ClubSquadStats players={club.topPlayers} />
                        </div>
                    </div>
                </div>

                <div className={styles.desktopOnly}>
                    <section className={styles.desktopTabsSection}>
                        <ClubTabs tabs={club.tabs} activeTab={activeTab} onTabChange={handleTabChange} />
                    </section>


                    <section className={styles.desktopStatsRow}>
                        <div className={styles.statsCard}>
                            <span className={styles.statsLabel}>Rank</span>
                            <div className={styles.statsValue}>#{club.rank}</div>
                            <span className={styles.statsSub}>LEAGUE RANK</span>
                        </div>
                        <div className={styles.gaugeContainer}>
                            <span className={styles.statsLabel}>Value</span>
                            <ClubValueGauge value={club.value} />
                        </div>
                        <div className={styles.statsCard}>
                            <span className={styles.statsLabel}>Points</span>
                            <div className={styles.statsValue}>{club.points}</div>
                            <span className={styles.statsSub}>SEASON POINTS</span>
                        </div>
                    </section>

                    <div style={{ position: 'relative' }}>
                        <IdentityGate
                            isMember={isMember}
                            isRevealing={isRevealing}
                            email={email}
                            setEmail={setEmail}
                            emailError={emailError}
                            setEmailError={setEmailError}
                            isSubmitting={isSubmitting}
                            handleUnlock={handleUnlock}
                            isSyncing={isSyncing}
                        />

                        <div className={`${styles['content-reveal']} ${!isMember ? styles['blurred-content-locked'] : ''}`}>
                            <section className={styles.desktopMetricsRow}>
                                <ClubMetricSection title="Attack Pulse" variant="pulse" metrics={[
                                    { label: 'xG', value: club.technicalStats.xg },
                                    { label: 'Actual Goals', value: club.technicalStats.goals }
                                ]} />
                                <ClubMetricSection title="Defensive Integrity" variant="cards" metrics={[
                                    { label: 'Errors Leading to Goals', value: club.technicalStats.errorsLeadingToGoals },
                                    { label: 'Interceptions', value: club.technicalStats.interceptions }
                                ]} />
                            </section>

                            <section className={styles.desktopAnalyticsGridV2}>
                                <ClubStreakForm streak={club.streak} variant="desktop" />
                                <ClubLiveStanding teams={club.miniStandings} variant="desktop" />
                            </section>

                            <ClubSquadStats players={club.topPlayers} />
                        </div>
                    </div>
                </div>

                <div style={{ position: 'relative', marginTop: '24px' }}>
                    <ClubArticles articles={articles} isLoading={isArticlesLoading} />
                </div>
            </div>
        </main>
    );
}
